import { Box } from 'reflexbox';
import * as graphql from 'generated/graphql';

import DotScore from '../layout/DotScore';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';
import { useTranslation } from 'react-i18next';
import QuestionDisplay from '../questions/Question';
import GoalCard from 'goals/GoalCard';
import GoalListItemCompetency from 'packages/kakapo-ui-kit/components/goals/list/GoalListItemCompetency';
import AnswerCard from '../answers/AnswerCard';
import UnsharedAnswer from '../answers/UnsharedAnswer';
import { SubSectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import styled from 'styled-components';
import IrrelevantAnswer from '../answers/IrrelevantAnswer';

import { useState } from 'react';
import Delta from 'quill-delta';
import { Button, QuillInput } from '@treams/treams-ui';
import EmptyAnswer from '../answers/EmptyAnswer';
import AddGoalModal from '../modals/AddGoalModal';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import AnswerLine from '../answers/AnswerLine';
import useProtectedValue from 'conversations/conversation/hooks/useProtectedValue';
import { useDebouncedCallback } from 'use-debounce';
import { AddIcon } from '@treams/treams-icons';
import SaveLine from '../layout/SaveLine';
import useDetectPrint from 'conversations/evaluation-submission/hooks/useDetectPrint';
import Comments from '../comments/Comments';

const Label = styled(SubSectionTitle)`
	font-size: 14px;
	margin-bottom: 6px;
`;

const ScoreContainer = styled(Box)`
	padding: 15px 0 20px;
`;

const Clarification = styled(Box)``;

const GoalGroupReviewItemContainer = styled(Box)`
	margin-bottom: 20px;
`;

function GoalReviewItemAnswerInput({
	answer,
	item,
	participant,
}: {
	answer: graphql.GoalReviewItemAnswer;
	item: graphql.GoalReviewItem;
	participant: graphql.Participant;
}) {
	const autosave = answer.status === graphql.ConversationItemAnswerStatus.Draft;

	const [score, scoreMutations] = useProtectedValue(answer.score);

	const [clarification, clarificationMutations] = useProtectedValue(
		new Delta(answer.clarification),
	);

	const [answerGoalReviewItemMutation, { loading }] = graphql.useAnswerGoalReviewItemMutation();

	const saveNewValues = ({
		clarification: newClarificationValue,
		score: newScoreValue,
	}: {
		clarification: Delta;
		score: number | undefined | null;
	}) => {
		answerGoalReviewItemMutation({
			variables: {
				conversationItemID: item.conversationItemID,
				score: newScoreValue,
				clarification: newClarificationValue,
				goalID: answer.goal.id,
			},
		}).then((response) => {
			const answer = response.data?.answerGoalReviewItem;
			const scoreChanged = answer?.score !== newScoreValue;
			const clarificationChanged =
				newClarificationValue.diff(new Delta(answer?.clarification)).ops.length > 0;

			scoreMutations.setDirty(scoreChanged);
			clarificationMutations.setDirty(clarificationChanged);
		});
	};

	const debouncedSaveNewValues = useDebouncedCallback(saveNewValues, 400);

	const handleReset = () => {
		scoreMutations.reset();
		clarificationMutations.reset();
	};

	const handleScoreChange = async (score: number) => {
		scoreMutations.setValue(score);
		scoreMutations.setDirty(true);

		if (autosave) {
			saveNewValues({ score, clarification: clarification.value });
		}
	};

	const handleClarificationChange = async (clarification: Delta) => {
		clarificationMutations.setValue(clarification);
		clarificationMutations.setDirty(true);

		if (autosave) {
			debouncedSaveNewValues({ score: score.value, clarification });
		}
	};

	const levels = answer.scoreLevelSet.levels.map((level) => {
		return { enabled: true, level };
	});

	return (
		<AnswerLine
			user={participant.user}
			side={participant.role === graphql.ConversationRole.Requester ? 'left' : 'right'}
		>
			<AnswerCard>
				{/* @TODO: Translate */}
				<Label>Voortgang van dit doel:</Label>

				<ScoreContainer>
					<DotScore
						levels={levels!}
						role={answer.participant.role}
						score={score.value}
						editable
						onChange={handleScoreChange}
					/>
				</ScoreContainer>

				<Clarification>
					{/* @TODO: Translate */}
					<Label>Toelichting:</Label>
					<QuillInput
						debounce={false}
						onChange={handleClarificationChange}
						value={clarification.value}
					/>
				</Clarification>
			</AnswerCard>
			{(score.changed || clarification.changed) && (
				<SaveLine
					onSave={() =>
						saveNewValues({
							score: score.value,
							clarification: clarification.value,
						})
					}
					onDiscard={handleReset}
					changed={score.changed || clarification.changed}
					saving={loading}
					dirty={score.dirty || clarification.dirty}
					autosave={autosave}
				/>
			)}
		</AnswerLine>
	);
}

function GoalReviewItemAnswer({
	answer,
	item,
}: {
	answer: graphql.GoalReviewItemAnswer;
	item: graphql.GoalReviewItem;
}) {
	const isPrinting = useDetectPrint();

	const levels = answer.scoreLevelSet.levels.map((level) => {
		return { enabled: true, level };
	});
	const clarification = answer.clarification;

	if (!answer.relevant) {
		return <IrrelevantAnswer participant={answer.participant} />;
	}

	if (!answer.viewerCanView) {
		return <UnsharedAnswer participant={answer.participant} />;
	}

	if (answer.viewerCanUpdate && !isPrinting) {
		return (
			<GoalReviewItemAnswerInput item={item} answer={answer} participant={answer.participant} />
		);
	}

	if (typeof answer.score !== 'number' && clarification.ops.length === 0) {
		return <EmptyAnswer participant={answer.participant} />;
	}

	return (
		<AnswerLine
			user={answer.participant.user}
			side={answer.participant.role === graphql.ConversationRole.Requester ? 'left' : 'right'}
		>
			<AnswerCard>
				{/* @TODO: Translate */}
				<Label>Voortgang van dit doel:</Label>

				<ScoreContainer>
					<DotScore levels={levels!} role={answer.participant.role} score={answer.score} />
				</ScoreContainer>

				{clarification && (
					<Clarification>
						{/* @TODO: Translate */}
						<Label>Toelichting:</Label>
						<QuillText quill={clarification} />
					</Clarification>
				)}
			</AnswerCard>
		</AnswerLine>
	);
}

function GoalGroupReviewItem({
	goal,
	item,
	answers,
}: {
	goal: graphql.Goal;
	item: graphql.GoalReviewItem;
	answers: graphql.GoalReviewItemAnswer[];
}) {
	return (
		<GoalGroupReviewItemContainer>
			<GoalCard goal={goal} modal />
			{goal.competency && <GoalListItemCompetency competency={goal.competency} />}

			{answers.map((answer) => (
				<GoalReviewItemAnswer item={item} answer={answer} key={answer.id} />
			))}
		</GoalGroupReviewItemContainer>
	);
}

export default function GoalReviewItem(props: {
	item: graphql.GoalReviewItem;
	participants: graphql.Participant[];
}) {
	const isPrinting = useDetectPrint();
	const [showAddGoalModal, setShowAddGoalModal] = useState(false);
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const [answers, setAnswers] = useState<graphql.GoalReviewItemAnswer[]>(props.item.answers);

	const [answerGoalReviewItemMutation, { loading: adding }] =
		graphql.useAnswerGoalReviewItemMutation();

	const handleAddGoal = (goal: graphql.Goal) => {
		answerGoalReviewItemMutation({
			variables: {
				conversationItemID: props.item.conversationItemID,
				goalID: goal.id,
			},
		}).then((result) => {
			if (result.data) {
				const answer = result.data.answerGoalReviewItem as graphql.GoalReviewItemAnswer;

				setAnswers([...answers, answer]);
				setShowAddGoalModal(false);
			}
		});
	};

	const answersGroupedByGoal = answers
		.reduce<
			{
				goal: graphql.GoalReviewItemAnswer['goal'];
				answers: graphql.GoalReviewItemAnswer[];
			}[]
		>((acc, answer) => {
			const foundIndex = acc.findIndex(({ goal }) => goal.id === answer.goal.id);

			if (foundIndex !== -1) {
				acc[foundIndex].answers.push(answer);
			} else {
				acc.push({
					goal: answer.goal,
					answers: [answer],
				});
			}

			return acc;
		}, [])
		.sort((a, b) => localize(a.goal.title).text.localeCompare(localize(b.goal.title).text));

	const selectedGoals = answersGroupedByGoal.map(({ goal }) => goal);

	const requester = props.participants.find(
		(participant) => participant.role === graphql.ConversationRole.Requester,
	);

	return (
		<QuestionDisplay
			title={t('evaluation.submission.reviewGoals.title')}
			number={props.item.number}
			description={t('evaluation.submission.reviewGoals.description')}
			rightSection={<Comments item={props.item} />}
		>
			{answersGroupedByGoal.map(({ goal, answers }) => (
				<GoalGroupReviewItem item={props.item} goal={goal} answers={answers} key={goal.id} />
			))}

			{/* @TODO: Translate */}
			{answersGroupedByGoal.length === 0 && <p>Nog geen doelen toegevoegd.</p>}

			{!isPrinting && (
				<Box display="inline-block">
					<Button
						onClick={() => setShowAddGoalModal(true)}
						icon={<AddIcon />}
						title={'Doelen toevoegen'}
						size="large"
					/>
				</Box>
			)}

			<AddGoalModal
				user={requester!.user}
				onDismiss={() => setShowAddGoalModal(false)}
				onAddGoal={handleAddGoal}
				selectedGoals={selectedGoals}
				adding={adding}
				opened={showAddGoalModal}
			/>
		</QuestionDisplay>
	);
}
